import { useQueryClient } from '@tanstack/react-query';
import { useCreateWhatsAppMessage } from 'src/modules/shared/messaging/hooks/api/whatsapp-message-mutation';
import { createOptimisticMessage } from 'src/modules/shared/messaging/utils/optimistic-whatsapp-message.util';
import { beginMessageMutationOrdering } from 'src/modules/shared/realtime/communications/utils/cache/event-order.util';
import { cancelMessageMutationReads } from '../../../utils/cache/message-mutation-reads.util';
import { refreshWhatsAppAvailability } from 'src/modules/shared/utils/integrations/refresh-whatsapp-availability.util';
import type {
  OutboundMessageCallbacks,
  OutboundMessageSubmission,
} from '../../../types/whatsapp/outbound-message.types';
import { enqueueMessageSend } from '../../../services/whatsapp/message-send-queue.service';
import {
  OUTBOUND_MESSAGE_MUTATION_KEY,
  getOutboundMessageDestination,
  getOutboundSupportScope,
} from '../../../utils/whatsapp/outbound-message.util';

/**
 * Owns independent optimistic sends while serializing HTTP requests per destination.
 * The mutation cache keeps submissions alive across composer replacement and drops
 * them on account reset; queued requests check that ownership before starting.
 * @param callbacks - Cache reconciliation and user-facing failure handling.
 * @returns Immediate submit action; pending state belongs to each timeline item.
 */
export const useSendConversationWhatsAppMessage = (
  callbacks: OutboundMessageCallbacks
) => {
  const queryClient = useQueryClient();
  const owns = (submission: OutboundMessageSubmission) =>
    submission.supportScope === getOutboundSupportScope() &&
    queryClient
      .getMutationCache()
      .getAll()
      .some((mutation) => {
        const variables: unknown = mutation.state.variables;
        return variables === submission;
      });
  const mutation = useCreateWhatsAppMessage({
    mutationKey: OUTBOUND_MESSAGE_MUTATION_KEY,
    prepareSubmission: (params): OutboundMessageSubmission => ({
      kind: 'communications.whatsapp.send',
      supportScope: getOutboundSupportScope(),
      params,
      optimisticMessage: createOptimisticMessage(params),
    }),
    dispatch: (execute, submission) =>
      enqueueMessageSend(
        queryClient,
        getOutboundMessageDestination(submission),
        async () => {
          if (!owns(submission)) {
            throw new DOMException(
              'Send cancelled after account reset',
              'AbortError'
            );
          }
          const controller = new AbortController();
          const unsubscribe = queryClient
            .getMutationCache()
            .subscribe((event) => {
              if (
                event.type === 'removed' &&
                event.mutation.state.variables === submission
              ) {
                controller.abort();
              }
            });
          const cancelAfterTenantChange = () => {
            if (!owns(submission)) controller.abort();
          };
          window.addEventListener('storage', cancelAfterTenantChange);
          try {
            return await execute(controller.signal);
          } finally {
            unsubscribe();
            window.removeEventListener('storage', cancelAfterTenantChange);
          }
        }
      ),
    onMutate: (submission) => {
      if (!owns(submission)) {
        throw new DOMException(
          'Send cancelled after account reset',
          'AbortError'
        );
      }
      const context = {
        queryClient,
        params: submission.params,
        optimisticMessage: submission.optimisticMessage,
        messageOrdering: beginMessageMutationOrdering(
          queryClient,
          submission.optimisticMessage
        ),
        interruptedInitialReads: cancelMessageMutationReads({
          queryClient,
          params: submission.params,
        }),
      };
      callbacks.onUpdate(submission.optimisticMessage, context);
      return context;
    },
    onSuccess: (message, submission, context) => {
      if (!owns(submission)) return;
      callbacks.onSuccess(message, context);
      void queryClient.invalidateQueries({
        queryKey: ['crm/funnel-channel-sessions'],
      });
    },
    onError: (error, submission, context) => {
      if (!owns(submission)) return;
      refreshWhatsAppAvailability(queryClient);
      callbacks.onError(
        error,
        context ?? {
          queryClient,
          params: submission.params,
          optimisticMessage: submission.optimisticMessage,
        }
      );
    },
    onSettled: (_message, _error, submission, context) => {
      context?.messageOrdering.dispose();
      if (!owns(submission)) return;
      void queryClient.invalidateQueries({
        predicate: (query) =>
          Boolean(context?.interruptedInitialReads.includes(query)) &&
          query.state.data === undefined,
        refetchType: 'active',
      });
    },
  });
  return {
    sendMessage: mutation.createWhatsAppMessage,
  };
};
